import { useContext, useEffect } from 'react';
import { Control, FieldValues, UseFormGetValues, UseFormSetValue, useWatch } from 'react-hook-form';

import { GlobaledContext } from '@/shared/global';
import { Country, State } from '@/shared/global/context/config';
import { getB2BCountries } from '@/shared/service/b2b';
const { VITE_B2B_CA_DOMAINS } = import.meta.env;

const useSetCountry = () => {
  const {
    state: { countriesList },
    dispatch,
  } = useContext(GlobaledContext);

  useEffect(() => {
    const init = async () => {
      if (countriesList && !countriesList.length) {
        let { countries } = await getB2BCountries();
        const isCAStore =
            VITE_B2B_CA_DOMAINS.split(',').map((s: any) => s.trim()).indexOf(window.location.host) !== -1;
        const countryListFull = countries;
        countries = [];
        countryListFull.forEach((field: any) => {
          if ((isCAStore && field.countryCode === 'CA') || (!isCAStore && field.countryCode !== 'CA')) {
            countries.push(field);
          }
        });
        var firstPositionCountryCode = "US";
        countries.sort(function(x: any,y: any){return x.countryCode == firstPositionCountryCode ? -1 : y.countryCode == firstPositionCountryCode ? 1 : 0; });

        dispatch({
          type: 'common',
          payload: {
            countriesList: countries,
          },
        });
      }
    };
    init();
    // ignore dispatch, it's not affecting any value from this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countriesList]);
};

interface FormFieldsProps extends Record<string, any> {
  name: string;
  label?: string;
  required?: boolean;
  fieldType?: string;
  default?: string | Array<any> | number;
  xs: number;
  variant: string;
  size: string;
  options?: any[];
}

interface GetCountryProps {
  setAddress: (arr: FormFieldsProps[]) => void;
  setValue: UseFormSetValue<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  addresses: FormFieldsProps[];
  control: Control;
}

const useGetCountry = ({
  setAddress,
  setValue,
  getValues,
  control,
  addresses,
}: GetCountryProps) => {
  const {
    state: { countriesList },
  } = useContext(GlobaledContext);

  const countryCode = useWatch({
    control,
    name: 'country',
  });

  // Populate country array
  useEffect(() => {
    const countriesFieldIndex = addresses.findIndex(
      (formFields: FormFieldsProps) => formFields.name === 'country',
    );
    if (countriesList?.length && countriesFieldIndex !== -1) {
      setAddress(
        addresses.map((addressField, addressFieldIndex) => {
          if (countriesFieldIndex === addressFieldIndex) {
            return { ...addressField, options: countriesList };
          }
          return addressField;
        }),
      );
    }
    // ignore addresses cause it will trigger loop array
    // ignore setAddress due it's no affecting any logic
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countriesList]);

  // Populate state array when the user change selected country
  useEffect(() => {
    if (!countryCode || !countriesList?.length) return;
    const stateList =
      countriesList.find((country: Country) => country.countryCode === countryCode)?.states || [];
    const stateFieldIndex = addresses.findIndex(
      (formFields: FormFieldsProps) => formFields.name === 'state',
    );
    if (stateFieldIndex !== -1) {
      setAddress(
        addresses.map((addressField, addressFieldIndex) => {
          if (stateFieldIndex === addressFieldIndex) {
            if (stateList.length > 0) {
              return {
                ...addressField,
                fieldType: 'dropdown',
                options: stateList,
              };
            }
            return { ...addressField, fieldType: 'text', options: [] };
          }
          return addressField;
        }),
      );
    }

    const stateVal = getValues('state');

    setValue(
      'state',
      stateVal &&
        countryCode &&
        (stateList.find((state: State) => state.stateName === stateVal) || stateList.length === 0)
        ? stateVal
        : '',
    );
    // ignore addresses cause it will trigger loop array
    // ignore setAddress, getValues and setValue due they're not affecting any value from this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode, countriesList]);
};

export { useGetCountry, useSetCountry };
